import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import type { StaffTab } from '../constants/staffTabs';

interface AuthStore {
   user?: { role?: string; staffPermissions?: string[] } | null;
}

/**
 * Guard an admin screen that has a grant of its own.
 *
 * `AdminRoute` only asks "admin or staff?" — enough while every staff surface
 * lived inside the Piso WiFi panel, which hides its own tabs. A screen with its
 * own address needs more: without this, any staff member could type the address
 * and land on a page whose every request comes back 403, which reads as broken
 * rather than as not-allowed.
 *
 * Use INSIDE AdminRoute, which has already handled sign-in and turned plain
 * operators away. Admins pass everything.
 */
const StaffTabRoute = ({ tab }: { tab: StaffTab | readonly StaffTab[] }) => {
   const { user } = useAuthStore() as AuthStore;
   const needed = Array.isArray(tab) ? tab : [tab as StaffTab];

   if (user?.role === 'admin') return <Outlet />;

   // Any one of the listed tabs is enough — a screen that holds several tabs is
   // worth opening as soon as one of them has something to show.
   const granted = user?.staffPermissions ?? [];
   if (needed.some((t) => granted.includes(t))) return <Outlet />;

   // Home, not another admin screen — a staff member may hold no other grant,
   // and bouncing them to a second panel they cannot use is no better.
   return <Navigate to="/home" replace />;
};

export default StaffTabRoute;
